import { SliderModal, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';

import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../../../routes/types';
import { useWithdrawalActions } from '../../../../../../store/customerDetail/withdrawal/actions';
import {
  newWithdrawalAtom,
  newWithdrawalCreatingAtom,
} from '../../../../../../store/customerDetail/withdrawal/atom';

import { CreatingWithdrawal } from './components/CreatingWithdrawal';
import { CreatingWithdrawalError } from './components/CreatingWithdrawalError';
import { Finished } from './components/Finished';
import { Header } from './components/Header';
import { withdrawalSteps } from './constants';

export const NewWithdrawal: React.FC = () => {
  const [step, setStep] = useState<keyof typeof withdrawalSteps>('accountAndValue');
  const [isFinished, setIsFinished] = useState(false);
  const [hasError, setHasError] = useState(false);
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const resetNewWithdrawalAtom = useResetRecoilState(newWithdrawalAtom);
  const { createWithdrawal } = useWithdrawalActions();
  const withdrawalState = useRecoilValue(newWithdrawalAtom);
  const route = useRoute<RouteProp<SignedInRootStackParamList, 'NewWithdrawal'>>();
  const isCreatingWithdrawal = useRecoilValue(newWithdrawalCreatingAtom);

  const handleGoback = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  const handleFinished = useCallback(async () => {
    const success = await createWithdrawal(
      route.params.accountId,
      withdrawalState.value,
      withdrawalState.withdrawalDate as Date,
      withdrawalState.selectedBank as number,
      withdrawalState.token
    );

    if (success) {
      setIsFinished(true);
    } else {
      setHasError(true);
    }
  }, [
    createWithdrawal,
    route.params.accountId,
    withdrawalState.selectedBank,
    withdrawalState.token,
    withdrawalState.value,
    withdrawalState.withdrawalDate,
  ]);

  const handleReset = useCallback(() => {
    setStep('accountAndValue');
    setIsFinished(false);
    setHasError(false);
    resetNewWithdrawalAtom();
  }, [resetNewWithdrawalAtom]);

  const handleNextStep = useCallback(() => {
    setStep('summaryAndTokenValidation');
  }, []);

  const handlePreviousStep = useCallback(() => {
    setStep('accountAndValue');
  }, []);

  const handleRetry = useCallback(() => {
    setStep('accountAndValue');
    setHasError(false);
  }, []);

  const StepComponent = withdrawalSteps[step];
  const canPreviousStep = step === 'summaryAndTokenValidation' && !isFinished;
  const width = useBreakpointValue({
    base: 1,
    sm: 0.5,
    lg: 0.3,
  });

  useEffect(() => {
    handleReset();

    return () => handleReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let content: JSX.Element;

  if (isCreatingWithdrawal) {
    content = <CreatingWithdrawal />;
  } else if (hasError) {
    content = <CreatingWithdrawalError onRetry={handleRetry} onReset={handleReset} />;
  } else {
    content = (
      <>
        {!isFinished && (
          <StepComponent
            accountId={route.params.accountId}
            customerId={route.params.customerId}
            accountNumber={route.params.accountNumber}
            onNextStep={handleNextStep}
            onFinish={handleFinished}
            onCancel={handleGoback}
          />
        )}
        {isFinished && <Finished onReset={handleReset} onGoBack={handleGoback} />}
      </>
    );
  }

  return (
    <SliderModal width={width}>
      <VStack padding={Spacing['inset.sm']} space={Spacing['stack.xxxs']}>
        <Header
          canPreviousStep={canPreviousStep}
          onPreviousStep={handlePreviousStep}
          onGoBack={handleGoback}
          hideSteps={isFinished || hasError}
        />
        {content}
      </VStack>
    </SliderModal>
  );
};
